import React from 'react';
import {styled, CircularProgress} from '@mui/material';
import {Deferred} from '@practicegenius/pg-ui/src/components/Deferred';
import {FetchFailureMessage} from '@practicegenius/pg-ui/src/components/FetchFailureMessage';
import {useUserProfileQuery} from './queries';
import {UserProfile} from './data';

const Context = React.createContext<UserProfile | undefined>(undefined);

const SpinnerRoot = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

export const useUserProfile = () => {
  const ctx = React.useContext(Context);

  if (!ctx) {
    throw new Error(
      'useUserProfile must be used within a UserProfileProvider!',
    );
  }

  return ctx;
};

export const UserProfileProvider: React.FCC<{
  /** Show error message if fetch fails */
  showErrorMessage?: boolean;
  /** Redirect to this URL if fetch fails */
  redirectOnError?: string;
}> = ({children, redirectOnError, showErrorMessage = false}) => {
  const profileQuery = useUserProfileQuery();

  switch (profileQuery.status) {
    case 'success':
      return (
        <Context.Provider value={profileQuery.data}>
          {children}
        </Context.Provider>
      );
    case 'error':
      if (redirectOnError) {
        window.location.href = redirectOnError;

        return null;
      }

      return showErrorMessage ? (
        <FetchFailureMessage retryFn={profileQuery.refetch} />
      ) : null;
    default:
      return (
        <Deferred>
          <SpinnerRoot>
            <CircularProgress />
          </SpinnerRoot>
        </Deferred>
      );
  }
};
